import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Fragment, useEffect, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Handshake,
  Shield,
  Users,
} from 'lucide-react';
import winclapLogo from '@/assets/winclap-logo.svg';
import { getAreaBadgeStyle } from '@/lib/brand-colors';
import { useAvatarUrl } from '@/hooks/useAvatarUrl';
import { useVisibleSections } from '@/hooks/useVisibleSections';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modo angosto (solo íconos) en desktop. En mobile el sidebar es un drawer. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

/** Secciones que arrancan abiertas si el usuario no las tocó. */
const DEFAULT_OPEN = ['Tickets', 'PeopleHub', 'IANative', 'FinOps'];

export function Sidebar({ isOpen, onClose, collapsed = false, onToggleCollapse }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const { singleNavItems, peopleTopLinks, peopleGroups, navSections, partnerships, userRoles } =
    useVisibleSections();

  // Estado de expansión con overrides explícitos: expanded = override ?? (default || rutaActiva).
  // Así colapsar una sección estando adentro FUNCIONA (antes el `|| rutaActiva`
  // pisaba el click y el desplegable "se trababa"). Al navegar, se limpian SOLO
  // los overrides de secciones activas bajo la nueva ruta (deep-links siempre
  // revelan dónde estás; los colapsos manuales del resto se respetan).
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  // href vacío = entrada "Próximamente" sin pantalla: nunca está activa
  // (sin este guard, '' + '/' matchea TODAS las rutas).
  const matches = (href: string) =>
    !!href && (location.pathname === href || location.pathname.startsWith(href + '/'));

  const isKeyActive = (key: string): boolean => {
    if (key === 'PeopleHub') {
      return (
        peopleTopLinks.some((l) => matches(l.href)) ||
        peopleGroups.some((g) => g.items.some((i) => matches(i.href)))
      );
    }
    const peopleGroup = peopleGroups.find((g) => g.key === key);
    if (peopleGroup) return peopleGroup.items.some((i) => matches(i.href));
    if (key === 'Partnerships') return location.pathname.startsWith('/partnerships');
    if (key.startsWith('partnerships:')) {
      return location.pathname.startsWith(`/partnerships/${key.split(':')[1]}`);
    }
    const section = navSections.find((s) => s.key === key);
    if (section) return section.items.some((i) => matches(i.href));
    return false;
  };

  const isExpanded = (key: string) =>
    overrides[key] ?? (DEFAULT_OPEN.includes(key) || isKeyActive(key));

  /** Colapsado no se renderiza ningún submenú: no hay ancho donde mostrarlo. */
  const isOpenNow = (key: string) => !collapsed && isExpanded(key);

  const toggleSection = (key: string) => {
    // Colapsado no hay lugar para el desplegable: primero se abre el sidebar
    // y se deja la sección expandida, en un solo click.
    if (collapsed) {
      onToggleCollapse?.();
      setOverrides((prev) => ({ ...prev, [key]: true }));
      return;
    }
    const current = isExpanded(key);
    setOverrides((prev) => ({ ...prev, [key]: !current }));
  };

  useEffect(() => {
    setOverrides((prev) => {
      const kept = Object.entries(prev).filter(([k]) => !isKeyActive(k));
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavClick = () => onClose();

  // Get primary role for user area badge
  const primaryRole = userRoles.find((r) => r !== 'user') ?? userRoles[0] ?? 'user';
  const areaStyle = getAreaBadgeStyle(primaryRole);
  const avatarUrl = useAvatarUrl();

  const renderSubItem = (
    subItem: { title: string; href: string; soon?: boolean },
    siblings: { title: string; href: string; soon?: boolean }[]
  ) => {
    // Programas anunciados sin sección todavía (Learning §3): apagados, sin link
    if (subItem.soon) {
      return (
        <div
          key={`soon:${subItem.title}`}
          className="flex items-center justify-between px-3 py-1.5 text-sm rounded-md text-white/30 cursor-default"
          aria-disabled
        >
          <span>{subItem.title}</span>
          <span className="text-[10px] uppercase tracking-wide text-white/25">{t('navigation.soon', 'Próximamente')}</span>
        </div>
      );
    }
    // Longest-prefix match so nested routes highlight their own entry
    const longestMatch = siblings
      .filter((i) => !i.soon && matches(i.href))
      .sort((a, b) => b.href.length - a.href.length)[0];
    const isActive = longestMatch?.href === subItem.href;
    return (
      <Link
        key={subItem.href}
        to={subItem.href}
        onClick={handleNavClick}
        className={cn(
          'flex items-center justify-between px-3 py-1.5 text-sm rounded-md transition-colors',
          isActive
            ? 'text-brand-green font-medium bg-white/5'
            : 'text-white/50 hover:text-white/80 hover:bg-white/5'
        )}
      >
        <span>{subItem.title}</span>
        {isActive && <span className="text-brand-green text-xs">→</span>}
      </Link>
    );
  };

  const groups: { key: 'workspace' | 'technology' | 'people' | 'programs' | 'finance' | 'oml' | 'org'; label: string }[] = [
    { key: 'workspace', label: 'WORKSPACE' },
    { key: 'technology', label: 'TECHNOLOGY' },
    { key: 'people', label: 'PEOPLE' },
    // Org Service (26/10/2026): organigrama y permisos, apartado propio como OML.
    { key: 'org', label: 'ORG' },
    { key: 'finance', label: 'FINANCE' },
    // Owned Media es un área propia (Sol 24/09): grupo OML con sus sub-apartados adentro.
    { key: 'oml', label: 'OML' },
    // Winclap Studio NO es un grupo: va como pestaña dentro del desplegable de People (Sol 19/10).
    { key: 'programs', label: 'PROGRAMS' },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen w-64 flex flex-col',
          'transition-[transform,width] duration-200',
          // Colapsado solo aplica en desktop: en mobile es un drawer completo.
          collapsed && 'md:w-16',
          'bg-brand-gray1',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0'
        )}
      >
        {/* Logo */}
        <Link
          to="/dashboard"
          onClick={handleNavClick}
          className={cn(
            'flex h-16 flex-col items-center justify-center border-b border-white/10 shrink-0 gap-1 hover:opacity-80 transition-opacity',
            collapsed ? 'md:px-2' : 'px-6'
          )}
        >
          <img
            src={winclapLogo}
            alt="Winclap"
            className={cn('w-auto object-contain invert', collapsed ? 'h-6 md:h-6' : 'h-7')}
          />
          {!collapsed && (
            <p className="text-[9px] font-medium tracking-[0.2em] text-white/25 uppercase">
              {t('layout.sidebar.back_office')}
            </p>
          )}
        </Link>

        {/* Navigation */}
        <nav className={cn('flex-1 overflow-y-auto py-4 space-y-1', collapsed ? 'px-2' : 'px-3')}>
          {/* Single items — Dashboard primero, después la pill People, después el resto */}
          {(() => {
            const renderSingleItem = (item: (typeof singleNavItems)[number]) => {
              const Icon = item.icon;
              const isActive = item.href === '/dashboard'
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={handleNavClick}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    collapsed && 'md:justify-center md:gap-0 md:px-0',
                    isActive
                      ? 'border-l-2 border-brand-green bg-white/5 text-white pl-[10px]'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="flex-1">{item.title}</span>}
                  {isActive && <span className="text-brand-green text-xs">→</span>}
                </Link>
              );
            };

            // Pill People — orden (Sol 20/08): Landing (independiente, todos los
            // clappers) → Performance → Usuarios → Dispositivos.
            const peopleHubActive = isKeyActive('PeopleHub');
            const peopleHubExpanded = isOpenNow('PeopleHub');

            return (
              <>
                {singleNavItems.slice(0, 1).map(renderSingleItem)}

                <div className="pt-1 pb-1">
                  <button
                    onClick={() => toggleSection('PeopleHub')}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2 text-sm font-semibold rounded-full transition-all',
                      collapsed && 'md:justify-center md:gap-0 md:px-0',
                      'bg-brand-purple-pill text-[#3B0764] hover:brightness-105 shadow-xs',
                      peopleHubActive && 'ring-2 ring-white/25'
                    )}
                  >
                    <Users className="h-4 w-4 shrink-0" />
                    {!collapsed && <span className="flex-1 text-left">People</span>}
                    {peopleHubExpanded
                      ? <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                      : <ChevronRight className="h-3.5 w-3.5 opacity-60" />
                    }
                  </button>

                  {peopleHubExpanded && (
                    <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                      {/* Primer nivel: Landing (todos) + Community Map (feedback 31/08) */}
                      {peopleTopLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={handleNavClick}
                          className={cn(
                            'flex items-center justify-between px-3 py-1.5 text-sm rounded-md transition-colors',
                            matches(link.href)
                              ? 'text-brand-green font-medium bg-white/5'
                              : 'text-white/60 hover:text-white/90 hover:bg-white/5'
                          )}
                        >
                          <span>{link.title}</span>
                          {matches(link.href) && <span className="text-brand-green text-xs">→</span>}
                        </Link>
                      ))}

                      {/* Grupos anidados con sus items */}
                      {peopleGroups.map((group) => {
                        const groupExpanded = isOpenNow(group.key);
                        const groupActive = isKeyActive(group.key);
                        return (
                          <div key={group.key}>
                            <button
                              onClick={() => toggleSection(group.key)}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                                groupActive ? 'text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                              )}
                            >
                              {!collapsed && <span className="flex-1 text-left">{group.title}</span>}
                              {groupExpanded
                                ? <ChevronDown className="h-3.5 w-3.5 text-white/40" />
                                : <ChevronRight className="h-3.5 w-3.5 text-white/40" />
                              }
                            </button>

                            {groupExpanded && (
                              <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                                {group.items.map((subItem) => renderSubItem(subItem, group.items))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {singleNavItems.slice(1).map(renderSingleItem)}
              </>
            );
          })()}

          {/* Grouped sections */}
          {groups.map(({ key, label }) => {
            const sections = navSections.filter((s) => s.group === key);
            if (sections.length === 0) return null;

            return (
              <div key={key} className="pt-4">
                {collapsed ? (
                  // Sin ancho para el título: una línea marca la separación.
                  <div className="mx-auto mb-2 h-px w-6 bg-white/10" aria-hidden />
                ) : (
                  <p className="px-3 pb-2 text-[10px] font-medium tracking-widest text-white/25 uppercase select-none">
                    {label}
                  </p>
                )}
                <div className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    const sectionExpanded = isOpenNow(section.key);
                    const isSectionActive = isKeyActive(section.key);

                    return (
                      <div key={section.key}>
                        <button
                          onClick={() => toggleSection(section.key)}
                          className={cn(
                            'flex w-full items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                            collapsed && 'md:justify-center md:gap-0 md:px-0',
                            isSectionActive
                              ? 'text-white'
                              : 'text-white/60 hover:text-white hover:bg-white/5'
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span className="flex-1 text-left">{section.title}</span>}
                          {sectionExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-white/40" />
                            : <ChevronRight className="h-3.5 w-3.5 text-white/40" />
                          }
                        </button>

                        {sectionExpanded && (
                          <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                            {section.items.map((subItem, i) => (
                              <Fragment key={`${subItem.heading ?? ''}:${subItem.href || subItem.title}`}>
                                {subItem.heading && subItem.heading !== section.items[i - 1]?.heading && (
                                  <p className={cn('px-3 pb-0.5 text-[10px] font-medium tracking-widest text-white/30 uppercase select-none', i > 0 && 'pt-2')}>
                                    {subItem.heading}
                                  </p>
                                )}
                                {renderSubItem(subItem, section.items)}
                              </Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Partnerships Hub — single collapsible dropdown (TikTok / Google / Meta) */}
          {partnerships.canView && (() => {
            const PARTNERS = [
              {
                slug: 'tiktok',
                name: 'TikTok',
                color: '#0066FF',
                key: 'partnerships:tiktok',
              },
              {
                slug: 'google',
                name: 'Google',
                color: '#DC2626',
                key: 'partnerships:google',
              },
              {
                slug: 'meta',
                name: 'Meta',
                color: '#9333EA',
                key: 'partnerships:meta',
              },
            ] as const;

            const SUB_LINKS = (slug: string) => [
              { title: t('layout.sidebar.partnerships_sub_hub'), href: `/partnerships/${slug}` },
              { title: t('layout.sidebar.partnerships_sub_products'), href: `/partnerships/${slug}/products` },
              { title: t('layout.sidebar.partnerships_sub_packages'), href: `/partnerships/${slug}/packages` },
              { title: t('layout.sidebar.partnerships_sub_cases'), href: `/partnerships/${slug}/cases` },
              { title: t('layout.sidebar.partnerships_sub_faq'), href: `/partnerships/${slug}/faq` },
            ];

            const hubExpanded = isOpenNow('Partnerships');
            const hubActive = location.pathname.startsWith('/partnerships');
            const governanceActive = location.pathname.startsWith('/partnerships/governance');

            return (
              <div className="pt-4">
                <button
                  onClick={() => toggleSection('Partnerships')}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    hubActive ? 'text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  )}
                >
                  <Handshake className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="flex-1 text-left">{t('layout.sidebar.partnerships_hub')}</span>}
                  {hubExpanded
                    ? <ChevronDown className="h-3.5 w-3.5 text-white/40" />
                    : <ChevronRight className="h-3.5 w-3.5 text-white/40" />
                  }
                </button>

                {hubExpanded && (
                  <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                    {PARTNERS.map(({ slug, name, color, key }) => {
                      const basePath = `/partnerships/${slug}`;
                      const partnerExpanded = isOpenNow(key);
                      const isActive = location.pathname.startsWith(basePath);
                      const subLinks = SUB_LINKS(slug);

                      return (
                        <div key={slug}>
                          <button
                            onClick={() => toggleSection(key)}
                            className={cn(
                              'flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                              isActive
                                ? 'text-white'
                                : 'text-white/60 hover:text-white hover:bg-white/5'
                            )}
                          >
                            <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            {!collapsed && <span className="flex-1 text-left">{name}</span>}
                            {partnerExpanded
                              ? <ChevronDown className="h-3.5 w-3.5 text-white/40" />
                              : <ChevronRight className="h-3.5 w-3.5 text-white/40" />
                            }
                          </button>

                          {partnerExpanded && (
                            <div className="mt-0.5 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                              {subLinks.map(sub => {
                                const isSubActive = location.pathname === sub.href ||
                                  (sub.href !== basePath && location.pathname.startsWith(sub.href + '/'));
                                return (
                                  <Link
                                    key={sub.href}
                                    to={sub.href}
                                    onClick={handleNavClick}
                                    className={cn(
                                      'flex items-center justify-between px-3 py-1.5 text-sm rounded-md transition-colors group',
                                      isSubActive
                                        ? 'text-brand-green font-medium bg-white/5'
                                        : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                                    )}
                                  >
                                    <span>{sub.title}</span>
                                    {isSubActive && <span className="text-brand-green text-xs">→</span>}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {partnerships.canAdminGovernance && (
                      <Link
                        to="/partnerships/governance"
                        onClick={handleNavClick}
                        className={cn(
                          'flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                          governanceActive
                            ? 'text-brand-green bg-white/5'
                            : 'text-white/60 hover:text-white hover:bg-white/5'
                        )}
                      >
                        <Shield className="h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1 text-left">Governance</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </nav>

        {/* Colapsar / expandir — solo desktop (en mobile el sidebar es drawer) */}
        {onToggleCollapse && (
          <div className="hidden md:block shrink-0 border-t border-white/10 px-3 py-2">
            <button
              type="button"
              onClick={onToggleCollapse}
              title={collapsed ? t('layout.sidebar.expandir_menu') : t('layout.sidebar.colapsar_menu')}
              aria-label={collapsed ? t('layout.sidebar.expandir_menu') : t('layout.sidebar.colapsar_menu')}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                'text-white/50 transition-colors hover:bg-white/5 hover:text-white',
                collapsed && 'justify-center px-0'
              )}
            >
              {collapsed
                ? <PanelLeftOpen className="h-4 w-4 shrink-0" />
                : <PanelLeftClose className="h-4 w-4 shrink-0" />}
              {!collapsed && <span className="flex-1 text-left">{t('layout.sidebar.colapsar_menu')}</span>}
            </button>
          </div>
        )}

        {/* User card */}
        <div className="shrink-0 border-t border-white/10 p-3">
          <div
            className={cn(
              'flex items-center gap-3 rounded-lg bg-white/5 py-2.5',
              collapsed ? 'justify-center px-0' : 'px-3'
            )}
            title={collapsed ? `${user?.name ?? ''} · ${primaryRole}` : undefined}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-green text-white text-sm font-semibold shrink-0 overflow-hidden">
              {avatarUrl
                ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                : (user?.name?.charAt(0)?.toUpperCase() ?? '?')
              }
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-white">{user?.name}</p>
                  <p className="truncate text-xs text-white/40">{user?.email}</p>
                </div>
                <span
                  className="shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: areaStyle.bg,
                    color: areaStyle.text,
                  }}
                >
                  {primaryRole}
                </span>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
